import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import type {
  Kpi,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import type MarketPerformanceKpi from 'lumen/models/market-performance/kpi';
import type MarketSnapshotsOverviewRoute from 'lumen/routes/market/snapshots/overview';
import type { LoadedDataItems } from 'lumen/routes/market/snapshots/overview';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import {
  type DataPreferences,
  DEFAULT_DATA_PREFERENCES,
  DEFAULT_TABLE_LAYOUT_PREFERENCES,
  GROUP_BY,
  GROUP_COLUMNS_BY,
  type GroupBy,
  isGroupBy,
  type SnapshotViewConfig,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import { formatMonthDateRange } from 'lumen/utils/dates/format-month-date-range';
import {
  type ColumnSettings,
  defaultColumnSettings,
  getVisibleKpis,
  getVisibleMetricTypes,
} from 'lumen/utils/market-snapshots/column-settings';
import {
  buildSnapshotSidebarLists,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from 'lumen/utils/market-snapshots/defaults';
import { isSortedColumnHidden } from 'lumen/utils/market-snapshots/order-by';
import type { ModelFrom } from 'lumen/utils/routes';

/**
 * Shared query params, date range, grouping, sorting, pagination and
 * reorderable/hideable columns for the market snapshots views. Views extend
 * this with their `config` and their own preference actions.
 */
export default abstract class MarketSnapshotsBaseController extends Controller {
  // Overview is the only view; switch to the shared route's model once there is one.
  declare model: ModelFrom<MarketSnapshotsOverviewRoute>;

  // Class fields are not concatenated across subclasses; views must not redeclare this.
  queryParams = [
    'fromDate',
    'toDate',
    'groupBy',
    'orderBy',
    'page',
    'pageSize',
  ];

  // Not `dates`: that name is kept for the page shell's date args.
  @service('dates') declare datesService: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service('local-storage') declare localStorage: Services['local-storage'];
  // eslint-disable-next-line ember/no-unused-services -- used by subclasses
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  // Empty until the route's setupController reads the stored settings.
  @tracked columnSettings: ColumnSettings = defaultColumnSettings([]);
  @tracked dataPreferences: DataPreferences = DEFAULT_DATA_PREFERENCES;
  @tracked fromDate?: string;
  @tracked groupBy: GroupBy = GROUP_BY.PROPERTY;
  @tracked kpiColumnSettings: ColumnSettings<Kpi> = defaultColumnSettings([]);
  // Semantic sort: "field" ascending, "-field" descending (e.g. "otb.totals.revenue").
  // The route derives the API order_by from this plus sortOn/variance.
  @tracked orderBy = 'name';
  @tracked page = 1;
  @tracked pageSize = DEFAULT_PAGE_SIZE;
  @tracked tableLayoutPreferences: TableLayoutPreferences =
    DEFAULT_TABLE_LAYOUT_PREFERENCES;
  @tracked toDate?: string;

  abstract readonly config: SnapshotViewConfig;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  get areKpisLoading(): boolean {
    return this.model.loadDataItemsTask.isRunning && !this.kpiCurrentLoad;
  }

  get columnMetrics(): Record<string, MetricType> {
    return this.config.columns.metrics(this.dataPreferences);
  }

  get currency(): SupportedCurrencies {
    return this.hotelAndMarketService.market.currencyCode;
  }

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get datepickerSidebarLists(): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
    return buildSnapshotSidebarLists(
      this.hotelAndMarketService.getLocalTime(),
      this.intl,
      this.groupBy,
    );
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const today = this.hotelAndMarketService.getLocalTime();

    return {
      start: this.fromDate
        ? this.hotelAndMarketService.toLocalTime(this.fromDate)
        : today.startOf('month'),
      end: this.toDate
        ? this.hotelAndMarketService.toLocalTime(this.toDate)
        : today.endOf('month'),
    };
  }

  get isMonthView(): boolean {
    return this.groupBy === GROUP_BY.MONTH;
  }

  // The last load, only while it matches the selected market, dates and SDLY
  // column: pagination and sorting keep the KPIs, any of those changes (or a
  // failed one) leaves them empty.
  get kpiCurrentLoad(): LoadedDataItems | undefined {
    const result = this.model.loadDataItemsTask.lastSuccessful?.value;
    if (!result) {
      return undefined;
    }

    const { end, start } = this.dateRange;
    const isSelectedLoad =
      result.params.marketId === this.hotelAndMarketService.market.id &&
      result.params.fromDate === start.format('YYYY-MM-DD') &&
      result.params.toDate === end.format('YYYY-MM-DD') &&
      result.params.sdlyColumn === this.dataPreferences.sdlyColumn;

    return isSelectedLoad ? result : undefined;
  }

  get kpis(): MarketPerformanceKpi[] {
    return this.kpiCurrentLoad
      ? [...this.store.peekAll<MarketPerformanceKpi>('market-performance/kpi')]
      : [];
  }

  get maxDate(): Dayjs {
    const today = this.hotelAndMarketService.getLocalTime();
    return today.add(2, 'year').endOf('year');
  }

  get showingText(): string {
    const totalRecords = this.totalRecords ?? 0;

    if (totalRecords === 0) {
      return '';
    }

    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(totalRecords),
    });
  }

  get totalRecords(): number | null {
    return (
      this.model.loadDataItemsTask.lastSuccessful?.value?.meta?.count ?? null
    );
  }

  // Only KPI mode applies the KPI settings; categories mode shows every KPI of the view.
  get visibleKpis(): readonly Kpi[] {
    return this.tableLayoutPreferences.groupColumnsBy === GROUP_COLUMNS_BY.KPIS
      ? getVisibleKpis(this.kpiColumnSettings)
      : this.config.columns.kpis;
  }

  get visibleMetricTypes(): MetricType[] {
    return getVisibleMetricTypes(
      this.columnSettings,
      this.tableLayoutPreferences,
      this.columnMetrics,
    );
  }

  @action
  monthTriggerLabelFormatter(start: Dayjs, end: Dayjs): string {
    return formatMonthDateRange(
      start,
      end,
      this.datesService.partialDateFormat.YEAR_AND_MONTH_LONG,
    );
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1;
  }

  @action
  onSort(orderBy: string): void {
    this.orderBy = orderBy;
    this.page = 1;
  }

  @action
  setGroupBy(groupBy: string): void {
    // orderBy stays in the URL on purpose so switching back restores the sort.
    // Date snapping happens in the transition validator, not here.
    if (!isGroupBy(groupBy)) {
      return;
    }

    if (groupBy === GROUP_BY.MONTH && !this.isMonthView) {
      const today = this.hotelAndMarketService.getLocalTime();
      this.fromDate = today.startOf('year').format('YYYY-MM-DD');
      this.toDate = today.endOf('year').format('YYYY-MM-DD');
    }

    this.groupBy = groupBy;
    this.page = 1;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  updateDateRange(range: { end: Dayjs | null; start: Dayjs }): void {
    this.fromDate = range.start.format('YYYY-MM-DD');
    this.toDate = (range.end ?? range.start).format('YYYY-MM-DD');
  }

  @action
  updateTableLayoutPreferences(
    preferences: TableLayoutPreferences,
    columns: { kpiSettings: ColumnSettings<Kpi>; settings: ColumnSettings },
  ): void {
    const { storageKeys } = this.config.columns;
    const sortOnChanged =
      preferences.sortOn !== this.tableLayoutPreferences.sortOn;

    this.tableLayoutPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
      JSON.stringify(preferences),
    );

    this.columnSettings = columns.settings;
    this.localStorage.setItem(
      storageKeys.columns,
      JSON.stringify(columns.settings),
    );

    this.kpiColumnSettings = columns.kpiSettings;
    this.localStorage.setItem(
      storageKeys.kpiColumns,
      JSON.stringify(columns.kpiSettings),
    );

    // A sort on a now-hidden column would be left pointing at nothing visible.
    const sortedIsHidden = isSortedColumnHidden(this.orderBy, {
      isKpiMode: preferences.groupColumnsBy === GROUP_COLUMNS_BY.KPIS,
      visibleKpis: this.visibleKpis,
      visibleMetricTypes: this.visibleMetricTypes,
    });

    // sortOn change or hidden sorted column resets sort; the orderBy/page QP change refetches.
    if (sortOnChanged || sortedIsHidden) {
      this.onSort('name');
    }
  }
}
